import { isPlatformServer } from '@angular/common';
import { computed, effect, inject, PLATFORM_ID, Signal, signal } from '@angular/core';
import {
  EmptyFeatureResult,
  getState,
  patchState as originalPatchState,
  SignalStoreFeature,
  SignalStoreFeatureResult,
  WritableStateSource,
} from '@ngrx/signals';

export type Prettify<Type extends object> = {
  [Key in keyof Type]: Type[Key];
};

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__:
      | {
          connect: (options: { name: string }) => {
            send: (action: Action, state: Record<string, unknown>) => void;
          };
        }
      | undefined;
  }
}

export type Action = { type: string };

const storeRegistry = signal<Record<string, Signal<unknown>>>({});

let currentActionNames = new Set<string>();

let synchronizationInitialized = false;

function initSynchronization() {
  effect(() => {
    if (!connection) {
      return;
    }

    const stores = storeRegistry();
    const rootState: Record<string, unknown> = {};
    for (const name in stores) {
      const store = stores[name];
      rootState[name] = store();
    }

    const names = Array.from(currentActionNames);
    const type = names.length ? names.join(', ') : 'Store Update';
    currentActionNames = new Set<string>();

    connection.send({ type }, rootState);
  });
}

function getStoreSignal(store: unknown): Signal<unknown> {
  return computed(() => getState(store as Parameters<typeof getState>[0]));
}

type ConnectResponse = {
  send: (action: Action, state: Record<string, unknown>) => void;
};
let connection: ConnectResponse | undefined;

/**
 * required for testing. is not exported during build
 */
export function reset() {
  connection = undefined;
  synchronizationInitialized = false;
  storeRegistry.set({});
}

/**
 * @param name store's name as it should appear in the DevTools
 */
export function withDevtools<Input extends SignalStoreFeatureResult>(
  name: string,
): SignalStoreFeature<Input, EmptyFeatureResult> {
  return store => {
    const isServer = isPlatformServer(inject(PLATFORM_ID));
    if (isServer) {
      return store;
    }

    const extensions = window.__REDUX_DEVTOOLS_EXTENSION__;
    if (!extensions) {
      return store;
    }

    if (!connection) {
      connection = extensions.connect({
        name: 'NgRx Signal Store',
      });
    }

    const storeSignal = getStoreSignal(store);
    storeRegistry.update(value => ({
      ...value,
      [name]: storeSignal,
    }));

    if (!synchronizationInitialized) {
      initSynchronization();
      synchronizationInitialized = true;
    }

    return store;
  };
}

type UpdateStateFn = typeof originalPatchState extends (arg1: infer First, ...args: infer Rest) => infer Returner
  ? (state: First, action: string, ...rest: Rest) => Returner
  : never;

/**
 * Wrapper of `patchState` for DevTools integration. Next to updating the state,
 * it also sends the action to the DevTools.
 * @param stateSource state of Signal Store
 * @param action name of action how it will show in DevTools
 * @param updaters updater functions or objects
 */
export const updateState: UpdateStateFn = <State extends object>(
  stateSource: WritableStateSource<State>,
  action,
  ...updaters
) => {
  currentActionNames.add(action);
  return originalPatchState(stateSource, ...updaters);
};

/**
 * @deprecated Has been renamed to `updateState`
 */
export const patchState: UpdateStateFn = <State extends object>(state: WritableStateSource<State>, action, ...rest) => {
  updateState(state, action, ...rest);
};
